import React, { useState } from "react";
import { Link as RouterLink } from "react-router-dom";
import logo from "../../icons/landinglogo.svg";
import { Link, animateScroll as scroll } from "react-scroll";
import ContactUs from "../ContactUs";

const DEFAULT_NAV_LINKS = [
  { to: "hero", label: "Home" },
  { to: "why-bellhopt", label: "Why Bellhopt" },
  { to: "integrations", label: "Integrations" },
  { to: "how-it-works", label: "How It Works" },
  { to: "service-areas", label: "Servicing Areas" },
  { to: "testimonials", label: "Testimonials" },
  { to: "faq", label: "FAQ" },
];

function Navbar({ navLinks = DEFAULT_NAV_LINKS, useHomeHashLinks = false }) {
  const [showContactModal, setShowContactModal] = useState(false);
  const navLinkClassName = "cursor-pointer hover:text-[#FF4D4D] transition-colors";

  const closeContactModal = () => {
    setShowContactModal(false);
  };

  const scrollToTop = () => {
    scroll.scrollToTop({
      duration: 500,
      smooth: true,
    });
  };

  return (
    <>
      <ContactUs
        showModal={showContactModal}
        handleCloseModal={closeContactModal}
        requireSupportType
      />

      {/* Desktop Navbar - Liquid Glass */}
      <nav className="w-full hidden laptop:block fixed top-0 left-0 right-0 z-40 flex justify-center items-start pointer-events-none">
        <div
          className="
            relative
            w-[92%] max-w-6xl mx-auto mt-4
            flex items-center justify-between gap-8
            px-6 py-3.5
            rounded-[999px]
            pointer-events-auto
            overflow-hidden
          "
        >
          {/* Liquid Glass Base Layer */}
          <div
            className="
              absolute inset-0
              bg-white/35
              backdrop-blur-[60px]
              -webkit-backdrop-blur-[60px]
              rounded-[999px]
              border border-[#E5E7EB]/80
              shadow-[0_8px_40px_rgba(0,0,0,0.12),0_2px_8px_rgba(0,0,0,0.08)]
            "
          />
          
          {/* Outer border for definition */}
          <div
            className="
              absolute inset-0
              rounded-[999px]
              border border-[#D1D5DB]/60
              pointer-events-none
            "
          />
          
          {/* Glass Reflection Layer */}
          <div
            className="
              absolute inset-0
              bg-gradient-to-b from-white/60 via-white/20 to-transparent
              rounded-[999px]
              pointer-events-none
            "
          />
          
          {/* Inner Glass Highlight */}
          <div
            className="
              absolute inset-[1px]
              rounded-[999px]
              border border-white/70
              pointer-events-none
            "
          />
          
          {/* Subtle gray tint for visibility */}
          <div
            className="
              absolute inset-0
              bg-gradient-to-br from-[#F9FAFB]/30 via-transparent to-transparent
              rounded-[999px]
              pointer-events-none
            "
          />
          
          {/* Content Container */}
          <div className="relative z-10 flex items-center justify-between w-full gap-8">
            {/* Logo */}
            {useHomeHashLinks ? (
              <RouterLink
                to="/"
                className="flex items-center gap-2 focus:outline-none relative z-10"
              >
                <img
                  src={logo}
                  alt="Bellhopt"
                  className="h-8 w-auto drop-shadow-[0_2px_8px_rgba(0,0,0,0.1)]"
                />
              </RouterLink>
            ) : (
              <button
                type="button"
                onClick={scrollToTop}
                className="flex items-center gap-2 focus:outline-none relative z-10"
              >
                <img
                  src={logo}
                  alt="Bellhopt"
                  className="h-8 w-auto drop-shadow-[0_2px_8px_rgba(0,0,0,0.1)]"
                />
              </button>
            )}

            {/* Center nav links */}
            <ul className="flex items-center gap-6 text-[12px] text-[#6B7280] relative z-10">
              {navLinks.map((link) => (
                <li key={link.to}>
                  {useHomeHashLinks ? (
                    <RouterLink to={`/#${link.to}`} className={navLinkClassName}>
                      {link.label}
                    </RouterLink>
                  ) : (
                    <Link
                      to={link.to}
                      smooth={true}
                      duration={500}
                      offset={-80}
                      className={navLinkClassName}
                    >
                      {link.label}
                    </Link>
                  )}
                </li>
              ))}
            </ul>

            {/* Right actions */}
            <div className="flex items-center gap-3 relative z-10">
              <a
                href="https://host.bellhopt.com/login"
                className="
                  px-5 py-2
                  rounded-full
                  border border-[#FF4D4D33]
                  text-[#FF4D4D]
                  text-[12px]
                  font-semibold
                  hover:bg-[#FF4D4D]
                  hover:text-white
                  transition-colors
                "
              >
                Host login
              </a>
              <button
                onClick={() => setShowContactModal(true)}
                className="
                  relative
                  px-5 py-2
                  rounded-full
                  overflow-hidden
                  bg-[#FF4D4D]
                  text-[#FFFFFF]
                  text-[12px]
                  font-semibold
                  shadow-[0_8px_22px_rgba(239,73,73,0.40)]
                  hover:bg-[#E63F3F]
                  transition-all
                "
              >
                <span className="relative z-10">Contact us</span>
                {/* Shine effect on hover */}
                <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent -translate-x-full hover:translate-x-full transition-transform duration-700" />
              </button>
            </div>
          </div>
        </div>
      </nav>

      {/* Mobile/Tablet Navbar - Liquid Glass (Hidden, ResponsiveNavbar handles mobile) */}
      <nav className="hidden fixed top-0 left-0 right-0 z-40 flex justify-center pointer-events-none">
        <div
          className="
            relative
            w-[92%] max-w-6xl mt-4
            flex items-center justify-between gap-8
            px-6 py-3
            rounded-[999px]
            pointer-events-auto
            overflow-hidden
          "
        >
          {/* Liquid Glass Base Layer */}
          <div
            className="
              absolute inset-0
              bg-white/35
              backdrop-blur-[60px]
              -webkit-backdrop-blur-[60px]
              rounded-[999px]
              border border-[#E5E7EB]/80
              shadow-[0_8px_40px_rgba(0,0,0,0.12),0_2px_8px_rgba(0,0,0,0.08)]
            "
          />
          
          {/* Outer border for definition */}
          <div
            className="
              absolute inset-0
              rounded-[999px]
              border border-[#D1D5DB]/60
              pointer-events-none
            "
          />
          
          {/* Glass Reflection Layer */}
          <div
            className="
              absolute inset-0
              bg-gradient-to-b from-white/60 via-white/20 to-transparent
              rounded-[999px]
              pointer-events-none
            "
          />
          
          {/* Inner Glass Highlight */}
          <div
            className="
              absolute inset-[1px]
              rounded-[999px]
              border border-white/70
              pointer-events-none
            "
          />
          
          {/* Subtle gray tint for visibility */}
          <div
            className="
              absolute inset-0
              bg-gradient-to-br from-[#F9FAFB]/30 via-transparent to-transparent
              rounded-[999px]
              pointer-events-none
            "
          />
          
          {/* Content Container */}
          <div className="relative z-10 flex items-center justify-between w-full">
            {/* Logo */}
            <button
              onClick={scrollToTop}
              className="flex items-center gap-2 focus:outline-none relative z-10"
            >
              <img
                src={logo}
                alt="Bellhopt"
                className="h-8 w-auto drop-shadow-[0_2px_8px_rgba(0,0,0,0.1)]"
              />
            </button>

            {/* Center nav links */}
            <ul className="flex items-center gap-6 text-[12px] text-[#6B7280] relative z-10">
              <li>
                <Link
                  to="hero"
                  smooth={true}
                  duration={500}
                  offset={-80}
                  className="cursor-pointer hover:text-[#FF4D4D] transition-colors"
                >
                  Home
                </Link>
              </li>
              <li>
                <Link
                  to="why-bellhopt"
                  smooth={true}
                  duration={500}
                  offset={-80}
                  className="cursor-pointer hover:text-[#FF4D4D] transition-colors"
                >
                  Why Bellhopt
                </Link>
              </li>
              <li>
                <Link
                  to="integrations"
                  smooth={true}
                  duration={500}
                  offset={-80}
                  className="cursor-pointer hover:text-[#FF4D4D] transition-colors"
                >
                  Integrations
                </Link>
              </li>
              <li>
                <Link
                  to="how-it-works"
                  smooth={true}
                  duration={500}
                  offset={-80}
                  className="cursor-pointer hover:text-[#FF4D4D] transition-colors"
                >
                  How It Works
                </Link>
              </li>
              <li>
                <Link
                  to="service-areas"
                  smooth={true}
                  duration={500}
                  offset={-80}
                  className="cursor-pointer hover:text-[#FF4D4D] transition-colors"
                >
                  Servicing Areas
                </Link>
              </li>
              <li>
                <Link
                  to="testimonials"
                  smooth={true}
                  duration={500}
                  offset={-80}
                  className="cursor-pointer hover:text-[#FF4D4D] transition-colors"
                >
                  Testimonials
                </Link>
              </li>
              <li>
                <Link
                  to="faq"
                  smooth={true}
                  duration={500}
                  offset={-80}
                  className="cursor-pointer hover:text-[#FF4D4D] transition-colors"
                >
                  FAQ
                </Link>
              </li>
            </ul>

            {/* Right actions */}
            <div className="flex items-center gap-3 relative z-10">
              <button
                onClick={() => setShowContactModal(true)}
                className="
                  relative
                  px-5 py-2
                  rounded-full
                  overflow-hidden
                  bg-[#FF4D4D]
                  text-[#FFFFFF]
                  text-[12px]
                  font-semibold
                  shadow-[0_8px_22px_rgba(239,73,73,0.40)]
                  hover:bg-[#E63F3F]
                  transition-all
                "
              >
                <span className="relative z-10">Contact us</span>
                {/* Shine effect on hover */}
                <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent -translate-x-full hover:translate-x-full transition-transform duration-700" />
              </button>
            </div>
          </div>
        </div>
      </nav>
    </>
  );
}

export default Navbar;
